import type { BankAccountGroupSchema } from "@wayflyer/customer-bankconnect";
import { useEffect, useState } from "react";

import { findGroupByUuid } from "../banking-accounts/utils";

import { consumeReopenGroupIntent } from "./reopen-group-panel-storage";

interface UseReopenGroupPanelOnReturnProps {
  customerDomain: string;
  accountGroups: BankAccountGroupSchema[];
  invalidateBankingSources: () => void;
  onSelectGroup: (group: BankAccountGroupSchema) => void;
  // Mandatory account selection takes priority over reopening the group panel - deferred until
  // this clears rather than dropped, so the group panel still reopens once the customer is done.
  isConfirmAccountSelectionPending: boolean;
  // The confirm-accounts panel doesn't block the rest of the page, so the customer can open a
  // different group's panel while it's showing - don't clobber whatever they're looking at.
  isGroupDetailsPanelOpen: boolean;
}

interface UseReopenGroupPanelOnReturnResult {
  // The member snapshot from the consumed intent, if any - captured once at mount so a caller
  // can seed reassigned-connection tracking that would otherwise be lost to the redirect reload.
  pendingMemberUuidsBeforeConnect: string[] | null;
}

// Basiq/mocked connect redirects away from the app entirely, so a group's connect-instantly
// intent has to survive a full page reload to reopen that group's panel on return.
export const useReopenGroupPanelOnReturn = ({
  customerDomain,
  accountGroups,
  invalidateBankingSources,
  onSelectGroup,
  isConfirmAccountSelectionPending,
  isGroupDetailsPanelOpen,
}: UseReopenGroupPanelOnReturnProps): UseReopenGroupPanelOnReturnResult => {
  const [pendingIntent] = useState(() => consumeReopenGroupIntent(customerDomain));
  const [pendingGroupUuid, setPendingGroupUuid] = useState<string | null>(() => pendingIntent?.groupUuid ?? null);

  useEffect(() => {
    if (pendingGroupUuid) {
      // The connection may not have finished processing by the time this page's initial data
      // load fired, so force a refetch rather than relying on it having caught up already.
      invalidateBankingSources();
    }
    // Only ever acts on the intent read once at mount; not meant to re-run as things change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingGroupUuid || isConfirmAccountSelectionPending || isGroupDetailsPanelOpen) {
      return;
    }
    const group = findGroupByUuid(accountGroups, pendingGroupUuid);
    if (!group) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors the reactive open-on-data-ready pattern in bank-controller-context.tsx
    setPendingGroupUuid(null);
    onSelectGroup(group);
  }, [pendingGroupUuid, accountGroups, onSelectGroup, isConfirmAccountSelectionPending, isGroupDetailsPanelOpen]);

  return { pendingMemberUuidsBeforeConnect: pendingIntent?.memberUuidsBeforeConnect ?? null };
};
